import React, { useRef, useState } from "react";

// Hooks
import { useIntl } from "react-intl";
import { IconButton } from "@mui/material";

// Mui Components
import { Box, FormGroup, Typography, Theme, Checkbox, FormControlLabel } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import createStyles from "@mui/styles/createStyles";

// Components
import CustomIcon from "components/CustomIcon";
import icons from "enums/icons";

// Types
type CustomiseLabelsProps = {
    showRoles?: boolean;
    setShowRoles?: React.Dispatch<React.SetStateAction<boolean>>;
    showNames?: boolean;
    setShowNames?: React.Dispatch<React.SetStateAction<boolean>>;
    isBalanceChart?: boolean;
    isSwitchAllLabelsVisible?: boolean;
    showAllLabels?: boolean;
    setShowAllLabels?: React.Dispatch<React.SetStateAction<boolean>>;
    hideAllLabels?: boolean;
    setHideAllLabels?: React.Dispatch<React.SetStateAction<boolean>>;
    chartCircles?: boolean;
    handleToggleArchetype?: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        customLabelContainer: {
            borderRadius: theme.spacing(2.5, 2.5, 0, 0),
            position: "absolute",
            transition: "all 300ms",
            overflow: "hidden",
            bottom: -20,
            left: 0,
        },
        customLabelHeader: {
            background: `linear-gradient(90deg, ${theme.palette.button.active.from} 0%, ${theme.palette.button.active.to} 142.5%)`,
            color: theme.palette.common.white,
            padding: theme.spacing(0.5, 2),
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            cursor: "pointer",
            "&:hover": {
                background: `linear-gradient(90deg, ${theme.palette.button.hover1.from} 0%, ${theme.palette.button.hover1.to} 100%)`,
            },
            "& .MuiTypography-root": {
                fontSize: 14,
                color: theme.palette.common.white,
            },
            "& .MuiButtonBase-root": {
                color: theme.palette.common.white,
                "& .MuiSvgIcon-root": {
                    fontSize: 14,
                },
            },
        },
        customLabelBody: {
            background: theme.palette.background.blue,
            padding: theme.spacing(1.5, 2),
            fontSize: 12,
        },
        track: {
            backgroundColor: theme.palette.button.activeWhite,
            border: `solid 1px ${theme.palette.neutrals.main} !important`,
        },
        trackOn: {
            backgroundColor: `${theme.palette.button.activeWhite} !important`,
            border: `solid 1px ${theme.palette.neutrals.main} !important`,
            opacity: "1 !important",
        },
        thumb: {
            background: "#919191",
        },
        thumbOn: {
            background: `linear-gradient(94.61deg, ${theme.palette.button.active.from} 0%, ${theme.palette.button.active.to} 100%)`,
        },
        chartCircleCheckbox: {
            color: theme.palette.primary.dark,
            marginLeft: 0,
            width: "fit-content",
            "& .MuiTypography-root": {
                fontSize: 11,
                marginRight: theme.spacing(0.5),
                color: theme.palette.text.primary,
                [theme.breakpoints.between("wd", "xl")]: {
                    maxWidth: 140,
                },
            },
            "& .MuiButtonBase-root": {
                padding: theme.spacing(0.5),
            },
            "& .Mui-checked": {
                color: `linear-gradient(94.61deg, ${theme.palette.button.active.from} 0%, ${theme.palette.button.active.to} 100%)`,
            },
        },
        itemDisabled: {
            color: theme.palette.text.disabled,
        },
    })
);

const CustomiseLabels = ({
    showRoles,
    setShowRoles,
    showNames,
    setShowNames,
    isSwitchAllLabelsVisible,
    showAllLabels,
    setShowAllLabels,
    hideAllLabels,
    setHideAllLabels,
    chartCircles,
    handleToggleArchetype,
}: CustomiseLabelsProps): JSX.Element => {
    const intl = useIntl();
    const classes = useStyles();

    const [hide, setHide] = useState(true);

    // Refs needed to calculate the height the height of the customise labels panel
    const buttonRef = useRef<HTMLButtonElement>(null);
    const listRef = useRef<HTMLUListElement>(null);

    const buttonHeight = buttonRef.current?.clientHeight || 0;
    const listHeight = listRef.current?.clientHeight || 0;
    const totalHeight = buttonHeight + listHeight;

    const handleToggleCustomiseLabels = () => {
        setHide(!hide);
    };

    return (
        <Box
            sx={(theme) => ({
                width: 220,
                height: hide ? buttonHeight : totalHeight,
                borderRadius: theme.spacing(2.5, 2.5, 0, 0),
                position: "absolute",
                transition: "all 300ms",
                overflow: "hidden",
                bottom: -50,
                left: 0,
            })}
        >
            <Box
                sx={{
                    width: "inherit",
                    position: "absolute",
                    transition: "transform 250ms",
                    transform: `translate(0, ${hide ? `calc(100% - ${totalHeight}px)` : 0})`,
                }}
            >
                <Box className={classes.customLabelHeader} onClick={handleToggleCustomiseLabels} ref={buttonRef}>
                    <Typography>{intl.formatMessage({ id: "charts.customiseLabels.title" })}</Typography>
                    <IconButton>
                        <CustomIcon icon={hide ? icons.chevronUpThin : icons.chevronDownThin} />
                    </IconButton>
                </Box>
                <FormGroup className={classes.customLabelBody} ref={listRef}>
                    {/* Show Roles */}
                    {setShowRoles ? (
                        <FormControlLabel
                            label={intl.formatMessage({ id: "charts.customiseLabels.showRoles" })}
                            className={classes.chartCircleCheckbox}
                            control={
                                <Checkbox
                                    name="showRoles"
                                    checked={showRoles}
                                    onChange={() => setShowRoles(!showRoles)}
                                />
                            }
                        />
                    ) : null}

                    {/* Show Names */}
                    {setShowNames ? (
                        <FormControlLabel
                            label={intl.formatMessage({ id: "charts.customiseLabels.showNames" })}
                            className={classes.chartCircleCheckbox}
                            control={
                                <Checkbox
                                    name="showNames"
                                    checked={showNames}
                                    onChange={() => setShowNames && setShowNames(!showNames)}
                                />
                            }
                        />
                    ) : null}

                    {/* Reveal Labels Not Shown */}
                    {isSwitchAllLabelsVisible && setShowAllLabels ? (
                        <FormControlLabel
                            className={classes.chartCircleCheckbox}
                            disabled={!(showRoles || showNames)}
                            control={
                                <Checkbox
                                    checked={showAllLabels}
                                    onChange={() => setShowAllLabels(!showAllLabels)}
                                    name="showAllLabels"
                                />
                            }
                            label={intl.formatMessage({ id: "charts.customiseLabels.showAllLabels" })}
                        />
                    ) : null}

                    {/* Typical Archetype Locations */}
                    {handleToggleArchetype && (
                        <FormControlLabel
                            className={classes.chartCircleCheckbox}
                            control={
                                <Checkbox checked={chartCircles} onChange={handleToggleArchetype} name="chartCircles" />
                            }
                            label={intl.formatMessage({ id: "charts.balance.whatGoodLooksLike" })}
                        />
                    )}
                </FormGroup>
            </Box>
        </Box>
    );
};

export default CustomiseLabels;
